import type { ChatMessage, ThreadItem } from './chat-types';
import type { Pace } from './chat-pacing';
import { useEffect, useRef, useState } from 'react';
import { MAX_THREAD_LENGTH, PACES, prefersReducedMotion, typingTime } from './chat-pacing';

type UseChatParams = { pace?: Pace };
type SayOptions = { onDone?: () => void };
type ReplyParams = { text: string; then: () => void };
type BeginParams = { delay: number; then: () => void };

/**
 * The conversation machinery both chats share: a thread that accumulates, a typing
 * indicator, and messages that land one after another at a human pace.
 *
 * It knows nothing about announcements or signup. Whoever owns the conversation
 * decides what to say and when; this decides how it arrives.
 *
 * Every delay collapses to zero under prefers-reduced-motion, so the thread fills
 * instantly rather than drip-feeding for someone who asked for less movement.
 *
 * `pace` picks the timing set: see PACES.
 */
export const useChat = ({ pace = 'normal' }: UseChatParams = {}) => {
  const timing = PACES[pace];
  const [thread, setThread] = useState<ThreadItem[]>([]);
  const [typing, setTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const nextId = useRef(0);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, reduced ? 0 : ms));
  };

  /** Cancels everything in flight. The thread itself is left as it is. */
  const stop = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setTyping(false);
    setBusy(false);
  };

  // Timers must not outlive the screen: a message landing after unmount is a state
  // update on nothing, and after a route change it would be a ghost in the next chat.
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const push = (message: ChatMessage) => {
    const id = nextId.current++;
    // Capped so a visitor who keeps asking for more does not grow the DOM without limit.
    setThread((current) => [...current, { ...message, id }].slice(-MAX_THREAD_LENGTH));
  };

  const clear = () => setThread([]);

  /** A bubble from the visitor. Lands at once; only Pertento types. */
  const sayAsMe = (text: string) => push({ kind: 'text', from: 'me', text });

  /**
   * Runs `then` after a beat, busy from now rather than from when it starts. The gap
   * before the first message is part of the conversation, and nothing should be
   * offered in it: a suggestion pill under an empty thread asks a question nobody
   * has put yet.
   */
  const begin = ({ delay, then }: BeginParams) => {
    setBusy(true);
    later(then, delay);
  };

  /**
   * The visitor says something and Pertento answers after a beat. Busy from the
   * moment they tap, not from when the answer starts typing, so the suggestions
   * cannot be tapped again in the gap.
   */
  const reply = ({ text, then }: ReplyParams) => {
    sayAsMe(text);
    begin({ delay: timing.reply, then });
  };

  const say = (messages: readonly ChatMessage[], options: SayOptions = {}) => {
    setBusy(true);

    const run = (idx: number) => {
      const message = messages[idx];

      if (!message) {
        setTyping(false);
        setBusy(false);
        options.onDone?.();
        return;
      }

      // A date label is an annotation. It lands with the message after it rather
      // than getting its own typing indicator and pause.
      if (message.kind === 'date') {
        push(message);
        run(idx + 1);
        return;
      }

      setTyping(true);
      later(
        () => {
          setTyping(false);
          push(message);
          later(() => run(idx + 1), timing.between);
        },
        typingTime(message, pace),
      );
    };

    run(0);
  };

  return { thread, typing, busy, reduced, say, sayAsMe, reply, begin, clear, stop, later };
};

export type Chat = ReturnType<typeof useChat>;
